import Tables from 'src/class/Tables';
import Formatter from 'src/class/Formatter';
import UnitPriceErrorFormatter from '../UnitPriceErrorFormatter';
import SubTotalErrorFormatter from '../SubTotalErrorFormatter';
import IncrementRateFormatter from '../IncrementRateFormatter';
import i18n from 'src/i18n';

const isWorkerTotalRow = (row) => row && row.is_worker_total_row;

const getWorkerTotalValue = (row) => {
  if (row.worker_total_value !== undefined && row.worker_total_value !== null) {
    return row.worker_total_value;
  }

  if (row.subtotal !== undefined && row.subtotal !== null) return row.subtotal;

  return row.total_value;
};

const getWorkerFullName = (worker) => {
  if (!worker) return '';

  return `${worker.first_name || ''} ${worker.last_name || ''}`.trim();
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || isNaN(Number(value))) return '';

  return `$${Formatter.formatNumber(Number(value))}`;
};

const UnitPriceSalaryComplianceFormatter = {
  functional: true,
  render(h, context) {
    const { row, column } = context.props;

    if (isWorkerTotalRow(row) || !row.activity) return h('span', '');

    return h(UnitPriceErrorFormatter, { props: { row, column } });
  },
};

const SubTotalSalaryComplianceFormatter = {
  functional: true,
  render(h, context) {
    const { row, column } = context.props;

    if (isWorkerTotalRow(row)) return h('span', '');

    return h(SubTotalErrorFormatter, { props: { row, column } });
  },
};

const IncrementRatesSalaryComplianceFormatter = {
  functional: true,
  render(h, context) {
    const { row, column } = context.props;

    if (isWorkerTotalRow(row)) return h('span', '');

    return h(IncrementRateFormatter, { props: { row, column } });
  },
};

const configTable = () => {
  const Table = { ...Tables.Table };
  Table.tableColumns = [
    {
      prop: 'date_save',
      label: i18n.t('common_words.date'),
      minWidth: 150,
      align: 'center',
      fixed: true,
      formatter: (row, column) => {
        if (isWorkerTotalRow(row)) return i18n.t('common_words.total');

        return row[column.property];
      },
    },
    {
      prop: 'worker.code',
      label: i18n.t('common_words.code_worker'),
      minWidth: 150,
      fixed: true,
      formatter: (row) => {
        if (row.worker && row.worker.code) return row.worker.code;

        return '';
      },
    },
    {
      prop: 'worker.first_name',
      label: `${i18n.t('common_words.name')} ${i18n.t('labor.worker')}`,
      minWidth: 180,
      align: 'center',
      fixed: true,
      formatter: (row) => {
        return getWorkerFullName(row.worker);
      },
    },
    {
      prop: 'activity_code',
      label: i18n.t('common_words.activity'),
      minWidth: 100,
      align: 'center',
      formatter: (row, column) => {
        if (isWorkerTotalRow(row)) return '';

        return row[column.property];
      },
    },
    {
      prop: 'activity_name',
      label: `${i18n.t('common_words.name')} 
        ${i18n.t('common_words.from').toLowerCase()} 
        ${i18n.t('common_words.activity')}`,
      minWidth: 200,
      align: 'center',
      formatter: (row, column) => {
        if (isWorkerTotalRow(row)) return '';

        return row[column.property];
      },
    },
    {
      prop: 'quantity',
      label: i18n.t('common_words.quantity'),
      minWidth: 100,
      align: 'center',
      formatter: (row, column) => {
        if (isWorkerTotalRow(row)) return '';

        return row[column.property];
      },
    },
    {
      prop: 'number_hours',
      label: i18n.t('common_words.duration'),
      minWidth: 100,
      sortable: true,
      align: 'center',
      formatter: (row, column) => {
        if (isWorkerTotalRow(row)) return '';

        return row[column.property];
      },
    },
    {
      prop: 'activity',
      label: i18n.t('labor.unitary_value'),
      minWidth: 150,
      sortable: true,
      align: 'center',
      formatter: UnitPriceSalaryComplianceFormatter,
    },
    {
      prop: 'farm_name',
      label: `${i18n.t('common_words.name')} ${i18n.t('common_words.farm')}`,
      minWidth: 200,
      formatter: (row, column) => {
        if (isWorkerTotalRow(row)) return '';

        return row[column.property];
      },
    },
    {
      prop: 'lot_name',
      label: i18n.t('common_words.allotment'),
      minWidth: 120,
      align: 'center',
      formatter: (row, column) => {
        if (isWorkerTotalRow(row)) return '';

        return row[column.property];
      },
    },
    {
      prop: 'activity.unit_payment_name',
      label: i18n.t('labor.unit'),
      minWidth: 120,
      align: 'center',
      formatter: (row) => {
        if (isWorkerTotalRow(row)) return '';

        return row.activity && row.activity.unit_payment_name
          ? row.activity.unit_payment_name
          : '';
      },
    },
    {
      prop: 'subtotal',
      label: i18n.t('common_words.value'),
      minWidth: 180,
      align: 'center',
      formatter: SubTotalSalaryComplianceFormatter,
    },
    {
      prop: 'total_value',
      label: i18n.t('common_words.value_total'),
      minWidth: 150,
      align: 'center',
      formatter: (row, column) => {
        const value = isWorkerTotalRow(row)
          ? getWorkerTotalValue(row)
          : row[column.property];

        return formatCurrency(value);
      },
    },
    {
      prop: 'attach',
      label: i18n.t('common_words.attach'),
      minWidth: 150,
      align: 'center',
      formatter: (row, column) => {
        if (isWorkerTotalRow(row)) return '';

        return formatCurrency(row[column.property]);
      },
    },
    {
      prop: 'subtract',
      label: i18n.t('common_words.subtract'),
      minWidth: 150,
      align: 'center',
      formatter: (row, column) => {
        if (isWorkerTotalRow(row)) return '';

        return formatCurrency(row[column.property]);
      },
    },
    {
      prop: 'daily_minimum_wage',
      label: i18n.t('common_words.daily_minimum_wage'),
      minWidth: 180,
      align: 'center',
      formatter: (row, column) => {
        if (isWorkerTotalRow(row)) return '';

        return formatCurrency(row[column.property]);
      },
    },
    {
      prop: 'time_type',
      label: i18n.t('labor.hour_type'),
      minWidth: 220,
      formatter: (row, column) => {
        if (isWorkerTotalRow(row) || !row[column.property]) return '';

        const { price_format, name, price } = row[column.property];
        const pre = price_format === '$' ? price_format : '';
        const post = price_format === '%' ? price_format : '';
        let text = `${name} ${pre}${Formatter.formatNumber(
          Number(price)
        )}${post} adicional`;

        return text;
      },
    },
    {
      prop: 'increment_rates',
      label: i18n.t('common_words.increments'),
      minWidth: 250,
      formatter: IncrementRatesSalaryComplianceFormatter,
    },
    {
      prop: 'observations',
      label: i18n.t('common_words.observation'),
      minWidth: 250,
    },
  ];

  return { Table };
};

export default configTable;
